import React, { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Box,
  Divider,
  Alert,
  Snackbar,
  Collapse,
  Toolbar,
  Tabs,
  Tab
} from '@material-ui/core';
import { lighten } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  AddCircle as AddCircleIcon
} from '@material-ui/icons';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { toast } from 'react-toastify';
import { TreasureService } from './services/TreasureService';
import bindModel from '../../common/bindModel';
import AnimatedImageDisplay from './components/AnimatedImageDisplay';
import Setting from './components/Setting';

// Helper functions for date formatting
const formatDateTime = (timestamp) => {
  if (!timestamp) return 'Chưa set';
  const date = new Date(timestamp);
  return date.toLocaleString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (timestamp) => {
  if (!timestamp) return 'Chưa set';
  const date = new Date(timestamp);
  return date.toLocaleDateString('vi-VN');
};

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return 'Chưa set';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('vi-VN', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// Hàm xác định trạng thái dựa trên thời gian
const getTreasureStatus = (startTime, endTime) => {
  if (!startTime || !endTime) return 'Chưa set thời gian';
  
  const now = new Date().getTime();
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  
  if (start > now) {
    return 'Sắp diễn ra';
  } else if (end < now) {
    return 'Đã kết thúc';
  } else if (start <= now && end >= now) {
    return 'Đang hoạt động';
  } else {
    return 'Không xác định';
  }
};

const useRowStyles = makeStyles({
  root: { margin: 100 },
  paper: { 
    width: '95%', 
    marginBottom: 10,
    marginTop: 100,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  paperInTab: { 
    width: '100%', 
    marginBottom: 10,
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
  },
  collapseRow: {
    '& > td': {
      borderBottom: 'none',
      padding: 0,
    }
  },
  mainTable: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    '& tbody tr': {
      '&:hover': {
        backgroundColor: '#f5f5f5',
      }
    }
  },
  expandCell: {
    width: '50px',
    padding: '8px',
  },
  imageCell: {
    width: '80px',
    padding: '8px',
  },
  idCell: {
    width: '100px',
    padding: '8px',
  },
  nameCell: {
    width: '200px',
    padding: '8px',
  },
  quantityCell: {
    width: '120px',
    padding: '8px',
  },
  priceCell: {
    width: '120px',
    padding: '8px',
  },
  counterCell: {
    width: '100px',
    padding: '8px',
  },
  closeimageCell: {
    width: '80px',
    padding: '8px',
  },
  openimageCell: {
    width: '80px',
    padding: '8px',
  },
  displayTime: {
    width: '120px',
    padding: '8px',
  },
  specialAnimationUrl: {
    width: '120px',
    padding: '8px',
  },
  notiAppTimeCell: {
    width: '150px',
    padding: '8px',
  },
  startTimeCell: {
    width: '150px',
    padding: '8px',
  },
  endTimeCell: {
    width: '150px',
    padding: '8px',
  },
  statusCell: {
    width: '120px',
    padding: '8px',
  },
  rainPayoutRateCell: {
    width: '120px',
    padding: '8px',
  },
  rainRewardMinCell: {
    width: '120px',
    padding: '8px',
  },
  rainRewardMaxCell: {
    width: '120px',
    padding: '8px',
  }
});

function createData(id, name, imageUrl, animatedUrl, buyPrice, quantity, counter, valid, gifts, startTime, endTime, 
  closeImageUrl, specialAnimationUrl, displayTime, notiAppTime, openImageUrl, rainPayoutRate, rainRewardMin, rainRewardMax) {
  return {
    id,
    name,
    imageUrl,
    openImageUrl,
    animatedUrl,
    buyPrice,
    quantity: gifts.length, // Tự động tính quantity từ số lượng gift
    counter,
    valid,
    gifts: gifts.map(gift => ({
      id: gift.id,
      name: gift.name,
      imageUrl: gift.imageUrl,
      sellPrice: gift.sellPrice,
      buyPrice: gift.buyPrice,
      status: gift.status,
      animatedUrl: gift.animatedUrl,
      dropRate: gift.dropRate,
      kind: gift.kind
    })),
    startTime,
    endTime,
    closeImageUrl,
    specialAnimationUrl,
    displayTime,
    notiAppTime,
    rainPayoutRate,
    rainRewardMin,
    rainRewardMax
  };
}

// Log để kiểm tra số lượng elements - đã di chuyển vào component

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected, handleClickOpenDialog, onRemoveAccounts, totalRows } = props;

  return (
    <Toolbar className={classes.root}>
      <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
        Danh sách Rương Kho Báu
        <Typography variant="caption" style={{ display: 'block', color: '#666', marginTop: 4 }}>
          Tổng cộng: {totalRows} rương
        </Typography>
      </Typography>
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton 
            aria-label="delete"
            onClick={onRemoveAccounts}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Add Treasure">
          <IconButton 
            aria-label="add treasure"
            onClick={handleClickOpenDialog}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

function Row(props) {
  const { row, onRowDoubleClick, onDeleteTreasure, loading, onEditTreasure, isExpanded, onToggleExpand } = props;
  const classes = useRowStyles();

  return (
    <React.Fragment>
      <TableRow 
        className={classes.root}
        // onDoubleClick={() => onRowDoubleClick(row)}
        style={{ cursor: 'pointer' }}
      >
        <TableCell className={classes.expandCell}>
          <IconButton aria-label="expand row" size="small" onClick={() => onToggleExpand(row.id)}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
          <TableCell className={classes.imageCell}>
            {row.imageUrl ? (
              <img src={row.imageUrl} alt={row.name} style={{ width: 50, height: 50, objectFit: 'cover' }} />
            ) : (
              <div style={{ 
                width: '50px', 
                height: '50px', 
                background: '#f8f9fa',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#999',
                border: '1px dashed #ddd'
              }}>
                Không có
              </div>
            )}
          </TableCell>
          <TableCell className={classes.closeimageCell}>
            {row.closeImageUrl ? (
              <img src={row.closeImageUrl} alt={row.name} style={{ width: 50, height: 50, objectFit: 'cover' }} />
            ) : (
              <div style={{ 
                width: '50px', 
                height: '50px', 
                background: '#f8f9fa',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#999',
                border: '1px dashed #ddd'
              }}>
                Không có
              </div>
            )}
          </TableCell>
          <TableCell className={classes.openimageCell}>
            {row.openImageUrl ? (
              <img src={row.openImageUrl} alt={row.name} style={{ width: 50, height: 50, objectFit: 'cover' }} />
            ) : (
              <div style={{ 
                width: '50px', 
                height: '50px', 
                background: '#f8f9fa',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#999',
                border: '1px dashed #ddd'
              }}>
                Không có
              </div>
            )}
          </TableCell>
        <TableCell className={classes.idCell} align="center">{row.id}</TableCell>
        <TableCell className={classes.nameCell} align="center">{row.name}</TableCell>
        <TableCell className={classes.imageCell} align="center" style={{ padding: '8px' }}>
          {row.animatedUrl ? (
            <div 
              style={{ 
                width: '50px', 
                height: '50px', 
                background: '#f0f0f0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#666',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => {
                // Mở modal với SVGAPlayer
                const modal = window.open('', '_blank', 'width=600,height=400');
                modal.document.write(`
                  <html>
                    <head>
                      <title>SVGA Animation - ${row.name}</title>
                      <style>
                        body { 
                          margin: 0; 
                          padding: 20px; 
                          background: #000; 
                          color: white; 
                          font-family: Arial, sans-serif; 
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                          height: 100vh;
                        }
                        .close-btn { 
                          position: absolute; 
                          top: 10px; 
                          right: 10px; 
                          background: #f44336; 
                          color: white; 
                          border: none; 
                          padding: 8px 12px; 
                          border-radius: 4px; 
                          cursor: pointer; 
                          z-index: 1000;
                        }
                        .container { 
                          text-align: center; 
                          width: 100%;
                          height: 100%;
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                        }
                        .svga-container {
                          width: 400px;
                          height: 300px;
                          background: #fff;
                          border-radius: 8px;
                          overflow: hidden;
                          position: relative;
                        }
                        h3 { margin: 0 0 20px 0; color: #fff; }
                      </style>
                    </head>
                    <body>
                      <button class="close-btn" onclick="window.close()">✕ Đóng</button>
                      <div class="container">
                        <h3>🎬 ${row.name}</h3>
                        <div class="svga-container" id="svga-player"></div>
                      </div>
                      <script>
                        // Load SVGA library
                        const script = document.createElement('script');
                        script.src = 'https://unpkg.com/svgaplayerweb@2.3.1/build/svga.min.js';
                        script.onload = function() {
                          // Fix for "process is not defined" error
                          if (typeof window !== 'undefined' && !window.process) {
                            window.process = { env: { NODE_ENV: 'production' } };
                          }
                          
                          const player = new SVGA.Player('#svga-player');
                          const parser = new SVGA.Parser();
                          
                          parser.load('${row.animatedUrl}', function(videoItem) {
                            player.setVideoItem(videoItem);
                            player.startAnimation();
                          }, function(error) {
                            console.error('SVGA loading error:', error);
                            document.getElementById('svga-player').innerHTML = 
                              '<div style="padding: 20px; text-align: center; color: #666;">' +
                              '<p>❌ Không thể tải SVGA animation</p>' +
                              '<p style="font-size: 12px;">URL: ${row.animatedUrl}</p>' +
                              '</div>';
                          });
                        };
                        document.head.appendChild(script);
                      </script>
                    </body>
                  </html>
                `);
                modal.document.close();
              }}
              title="Click để xem ảnh động"
            >
              🎬
            </div>
          ) : (
            <div style={{ 
              width: '50px', 
              height: '50px', 
              background: '#f8f9fa',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: '#999',
              border: '1px dashed #ddd'
            }}>
              Không có
            </div>
          )}
        </TableCell>
        <TableCell className={classes.displayTime} align="center">
          {row.displayTime ? (
            <Chip
              label={`${row.displayTime}s`}
              style={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 'bold',
                fontSize: '11px',
                padding: '2px 6px'
              }}
              size="small"
            />
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Không có</span>
          )}
        </TableCell>
        <TableCell className={classes.specialAnimationUrl} align="center">
          {row.specialAnimationUrl ? (
            <div 
              style={{ 
                width: '50px', 
                height: '50px', 
                background: '#f0f0f0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#666',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => {
                // Mở modal với SVGAPlayer
                const modal = window.open('', '_blank', 'width=600,height=400');
                modal.document.write(`
                  <html>
                    <head>
                      <title>Special Animation - ${row.name}</title>
                      <style>
                        body { 
                          margin: 0; 
                          padding: 20px; 
                          background: #000; 
                          color: white; 
                          font-family: Arial, sans-serif; 
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                          height: 100vh;
                        }
                        .close-btn { 
                          position: absolute; 
                          top: 10px; 
                          right: 10px; 
                          background: #f44336; 
                          color: white; 
                          border: none; 
                          padding: 8px 12px; 
                          border-radius: 4px; 
                          cursor: pointer; 
                          z-index: 1000;
                        }
                        .container { 
                          text-align: center; 
                          width: 100%;
                          height: 100%;
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: center;
                        }
                        .svga-container {
                          width: 400px;
                          height: 300px;
                          background: #fff;
                          border-radius: 8px;
                          overflow: hidden;
                          position: relative;
                        }
                        h3 { margin: 0 0 20px 0; color: #fff; }
                      </style>
                    </head>
                    <body>
                      <button class="close-btn" onclick="window.close()">✕ Đóng</button>
                      <div class="container">
                        <h3>🎬 ${row.name}</h3>
                        <div class="svga-container" id="svga-player"></div>
                      </div>
                      <script>
                        // Load SVGA library
                        const script = document.createElement('script');
                        script.src = 'https://unpkg.com/svgaplayerweb@2.3.1/build/svga.min.js';
                        script.onload = function() {
                          // Fix for "process is not defined" error
                          if (typeof window !== 'undefined' && !window.process) {
                            window.process = { env: { NODE_ENV: 'production' } };
                          }
                          
                          const player = new SVGA.Player('#svga-player');
                          const parser = new SVGA.Parser();
                          
                          parser.load('${row.specialAnimationUrl}', function(videoItem) {
                            player.setVideoItem(videoItem);
                            player.startAnimation();
                          }, function(error) {
                            console.error('SVGA loading error:', error);
                            document.getElementById('svga-player').innerHTML = 
                              '<div style="padding: 20px; text-align: center; color: #666;">' +
                              '<p>❌ Không thể tải SVGA animation</p>' +
                              '<p style="font-size: 12px;">URL: ${row.specialAnimationUrl}</p>' +
                              '</div>';
                          });
                        };
                        document.head.appendChild(script);
                      </script>
                    </body>
                  </html>
                `);
                modal.document.close();
              }}
              title="Click để xem ảnh động đặc biệt"
            >
              🎬
            </div>
          ) : (
            <div style={{ 
              width: '50px', 
              height: '50px', 
              background: '#f8f9fa',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: '#999',
              border: '1px dashed #ddd'
            }}>
              Không có
            </div>
          )}
        </TableCell>
        <TableCell className={classes.quantityCell} align="center">{row.quantity}</TableCell>
        <TableCell className={classes.priceCell} align="center">{row.buyPrice}</TableCell>
        <TableCell className={classes.counterCell} align="center">{row.counter}</TableCell>
        <TableCell className={classes.notiAppTimeCell} align="center">
          {row.notiAppTime ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#9c27b0' }}>
                {row.notiAppTime}s
              </div>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Không hiện</span>
          )}
        </TableCell>
        <TableCell className={classes.startTimeCell} align="center">
          {row.startTime ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                {formatDateOnly(row.startTime)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {formatTimeOnly(row.startTime)}
              </div>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa set</span>
          )}
        </TableCell>
        <TableCell className={classes.endTimeCell} align="center">
          {row.endTime ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#f57c00' }}>
                {formatDateOnly(row.endTime)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {formatTimeOnly(row.endTime)}
              </div>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa set</span>
          )}
        </TableCell>
        <TableCell className={classes.rainPayoutRateCell} align="center">
          {row.rainPayoutRate !== undefined && row.rainPayoutRate !== null ? (
            <Chip
              label={`${row.rainPayoutRate}%`}
              style={{
                backgroundColor: '#e8f5e8',
                color: '#2e7d32',
                fontWeight: 'bold',
                fontSize: '11px',
                padding: '2px 6px'
              }}
              size="small"
            />
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa set</span>
          )}
        </TableCell>
        <TableCell className={classes.rainRewardMinCell} align="center">
          {row.rainRewardMin !== undefined && row.rainRewardMin !== null ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                {row.rainRewardMin}
              </div>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa set</span>
          )}
        </TableCell>
        <TableCell className={classes.rainRewardMaxCell} align="center">
          {row.rainRewardMax !== undefined && row.rainRewardMax !== null ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#f57c00' }}>
                {row.rainRewardMax}
              </div>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Chưa set</span>
          )}
        </TableCell>
        <TableCell className={classes.statusCell} align="center">
          {(() => {
            const status = getTreasureStatus(row.startTime, row.endTime);
            let statusColor = '#666';
            let backgroundColor = '#f5f5f5';
            
            switch (status) {
              case 'Sắp diễn ra':
                statusColor = '#1976d2';
                backgroundColor = '#e3f2fd';
                break;
              case 'Đang hoạt động':
                statusColor = '#2e7d32';
                backgroundColor = '#e8f5e8';
                break;
              case 'Đã kết thúc':
                statusColor = '#d32f2f';
                backgroundColor = '#ffebee';
                break;
              default:
                statusColor = '#666';
                backgroundColor = '#f5f5f5';
            }
            
            return (
              <Chip
                label={status}
                style={{
                  backgroundColor: backgroundColor,
                  color: statusColor,
                  fontWeight: 'bold',
                  fontSize: '12px',
                  padding: '4px 8px',
                  border: `1px solid ${statusColor}20`
                }}
                size="small"
              />
            );
          })()}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className={classes.collapseRow}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={18}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 16,
                  padding: '16px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: 4, fontSize: '1.25rem' }}>
                      📦 Thông tin chi tiết rương: {row.name}
                    </div>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      🆔 ID: {row.id} | 💰 Giá: {row.buyPrice} icoin | 🎁 Số lượng quà: {row.quantity} | 📊 Đã bán: {row.counter}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666', marginTop: 4 }}>
                      ⏰ Bắt đầu: {formatDateTime(row.startTime)} | 
                      🕐 Kết thúc: {formatDateTime(row.endTime)}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666', marginTop: 4 }}>
                      🌧️ Tỷ lệ trả thưởng: {row.rainPayoutRate || 0}% | 
                      💎 Phần thưởng: {row.rainRewardMin || 0} - {row.rainRewardMax || 0}
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Tooltip title="Chỉnh sửa rương này">
                      <IconButton 
                        aria-label="edit treasure"
                        onClick={() => onEditTreasure(row)}
                        style={{ 
                          backgroundColor: '#2196f3', 
                          color: 'white',
                          transition: 'all 0.2s ease',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#1976d2';
                          e.target.style.transform = 'scale(1.05)';
                          e.target.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#2196f3';
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.3)';
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={loading.delete ? "Đang xóa..." : "Xóa rương này"}>
                      <IconButton 
                        aria-label="delete treasure"
                        onClick={() => onDeleteTreasure(row)}
                        disabled={loading.delete}
                        style={{ 
                          backgroundColor: loading.delete ? '#ccc' : '#f44336', 
                          color: 'white',
                          transition: 'all 0.2s ease',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: loading.delete ? 'none' : '0 2px 8px rgba(244, 67, 54, 0.3)',
                          '&:hover': {
                            backgroundColor: loading.delete ? '#ccc' : '#d32f2f',
                            transform: loading.delete ? 'none' : 'scale(1.05)',
                            boxShadow: loading.delete ? 'none' : '0 4px 12px rgba(244, 67, 54, 0.4)'
                          },
                          '&:disabled': {
                            backgroundColor: '#ccc',
                            cursor: 'not-allowed',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        {loading.delete ? (
                          <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            border: '2px solid #fff', 
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <Table size="small" aria-label="purchases" style={{ marginTop: 10 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">STT</TableCell>
                      <TableCell align="center">Ảnh đại diện</TableCell>
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">Tên món quà</TableCell>
                      <TableCell align="center">Loại</TableCell>
                      <TableCell align="center">Giá bán</TableCell>
                      <TableCell align="center">Giá mua</TableCell>
                      <TableCell align="center">Trạng thái</TableCell>
                      <TableCell align="center">Ảnh động</TableCell>
                      <TableCell align="center">Tỷ lệ trúng</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.gifts.map((gift, index) => (
                      <TableRow key={gift.id}>
                        <TableCell component="th" scope="row" align="center">{index + 1}</TableCell>
                        <TableCell><img src={gift.imageUrl} alt={gift.name} style={{ width: 50, height: 50, objectFit: 'cover' }} /></TableCell>
                        <TableCell align="center">{gift.id}</TableCell>
                        <TableCell align="center">{gift.name}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(() => {
                              if (!gift.kind || gift.kind === '') {
                                return 'Không có giá trị';
                              } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                return 'Hiếm';
                              } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                return 'Bình thường';
                              } else {
                                return 'Không có giá trị';
                              }
                            })()}
                            style={{
                              backgroundColor: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '#f5f5f5';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                  return '#fff3e0';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '#e8f5e8';
                                } else {
                                  return '#f5f5f5';
                                }
                              })(),
                              color: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '#999';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                  return '#f57c00';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '#4caf50';
                                } else {
                                  return '#999';
                                }
                              })(),
                              fontWeight: 'bold',
                              fontSize: '11px',
                              padding: '2px 6px',
                              border: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '1px solid #99920';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                  return '1px solid #f57c0020';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '1px solid #4caf5020';
                                } else {
                                  return '1px solid #99920';
                                }
                              })()
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{gift.sellPrice}</TableCell>
                        <TableCell align="center">{gift.buyPrice || 0}</TableCell>
                        <TableCell align="center">{gift.status}</TableCell>
                        <TableCell align="center" style={{ padding: '8px' }}>
                          <AnimatedImageDisplay
                            animatedUrl={gift.animatedUrl}
                            giftName={gift.name}
                            size="small"
                            showButton={false}
                          />
                        </TableCell>
                        <TableCell align="center">{gift.dropRate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`treasure-tabpanel-${index}`}
      aria-labelledby={`treasure-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={0}>
          {children}
        </Box>
      )}
    </div>
  );
}

// TabsContainer component
function TabsContainer({ currentTab, setCurrentTab, children }) {
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <div>
      <Paper square>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="treasure management tabs"
        >
          <Tab 
            label="📦 Danh sách Rương Kho Báu" 
            id="treasure-tab-0"
            aria-controls="treasure-tabpanel-0"
          />
          <Tab 
            label="⚙️ Setting Mưa quà" 
            id="treasure-tab-1"
            aria-controls="treasure-tabpanel-1"
          />
        </Tabs>
      </Paper>
      {children}
    </div>
  );
}

// TreasureList component - chứa nội dung danh sách rương
function TreasureList({ 
  selected, 
  rows, 
  loading, 
  handleClickOpenDialog, 
  handleRemoveAccounts, 
  handleDeleteTreasure,
  expandedRow,
  handleToggleExpand
}) {
  const classes = useRowStyles();

  return (
    <TableContainer 
      component={Paper} 
      className={classes.paperInTab}
    >
      <EnhancedTableToolbar 
        numSelected={selected.length} 
        handleClickOpenDialog={() => handleClickOpenDialog(null)}
        onRemoveAccounts={handleRemoveAccounts}
        totalRows={rows.length}
      />
      <Table aria-label="collapsible table" className={classes.mainTable}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.expandCell} />
            <TableCell className={classes.imageCell} align="center">Rương</TableCell>
            <TableCell className={classes.closeimageCell} align="center">Rương đóng</TableCell>
            <TableCell className={classes.openImageCell} align="center">Rương mở</TableCell>
            <TableCell className={classes.idCell} align="center">ID</TableCell>
            <TableCell className={classes.nameCell} align="center">Tên Rương</TableCell>
            <TableCell className={classes.imageCell} align="center">Ảnh động khi mở</TableCell>
            <TableCell className={classes.displayTime} align="center">Thời gian hiển thị mở quà</TableCell>
            <TableCell className={classes.specialAnimationUrl} align="center">Toả khi mở rương</TableCell>
            <TableCell className={classes.quantityCell} align="center">Số lượng quà</TableCell>
            <TableCell className={classes.priceCell} align="center">Giá mua (icoin)</TableCell>
            <TableCell className={classes.counterCell} align="center">Đã bán</TableCell>
            <TableCell className={classes.notiAppTimeCell} align="center">Thời gian hiện noti quà hiếm</TableCell>
            <TableCell className={classes.startTimeCell} align="center">Thời gian bắt đầu</TableCell>
            <TableCell className={classes.endTimeCell} align="center">Thời gian kết thúc</TableCell>
            <TableCell className={classes.rainPayoutRateCell} align="center">Tỷ lệ thưởng mưa quà (%)</TableCell>
            <TableCell className={classes.rainRewardMinCell} align="center">Phần thưởng mưa tối thiểu</TableCell>
            <TableCell className={classes.rainRewardMaxCell} align="center">Phần thưởng mưa tối đa</TableCell>
            <TableCell className={classes.statusCell} align="center">Trạng thái</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <Row 
              key={row.id} 
              row={row} 
              onRowDoubleClick={handleClickOpenDialog}
              onDeleteTreasure={handleDeleteTreasure}
              onEditTreasure={handleClickOpenDialog}
              loading={loading}
              isExpanded={expandedRow === row.id}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}


function ManageTreasure() {
  const classes = useRowStyles();
  const [selected, setSelected] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState({
    fetch: false,
    save: false,
    delete: false,
    uploadImage: false,
    uploadAnimatedImage: false,
    uploadCloseImage: false,
    uploadOpenImage: false,
    uploadSpecialAnimation: false
  });
  const [buttonName, setButtonName] = useState("TẠO RƯƠNG");
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [newTreasure, setNewTreasure] = useState({
    id: null,
    name: '',
    imageUrl: '',
    animatedUrl: '',
    buyPrice: 0,
    quantity: 0,
    counter: 0,
    valid: false,
    gifts: [],
    startTime: new Date().getTime(),
    endTime: 253402189200000,
    closeImageUrl: '',
    openImageUrl: '',
    specialAnimationUrl: '',
    displayTime: 2,
    notiAppTime: 0,
    rainPayoutRate: 0,
    rainRewardMin: 0,
    rainRewardMax: 0
  });

  const [giftId, setGiftId] = useState('');

  const handleToggleExpand = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const getTreasureList = async () => {
    setLoading(prev => ({ ...prev, fetch: true }));
    try {
      const response = await TreasureService.getTreasureList();

      let treasures = response?.data?.treasures?.treasure ?? [];
      if (response.status === "OK" && Array.isArray(treasures)) {
        treasures = treasures.map((e) => 
          createData(e.id, e.name, e.imageUrl, e.animatedUrl, e.buyPrice, e.quantity, e.counter, e.valid, e.gifts, e.startTime, 
            e.endTime, e.closeImageUrl, e.specialAnimationUrl, e.displayTime, e.notiAppTime, e.openImageUrl, e.rainPayoutRate, e.rainRewardMin, e.rainRewardMax)
        );
        setRows(treasures);
        // toast.success(response.message);
      } else {
        toast.warning(response.message);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while fetching accounts");
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  const resetNewTreasure = () => {
    setNewTreasure({
      id: null,
      name: '',
      imageUrl: '',
      animatedUrl: '',
      buyPrice: 0,
      quantity: 0, // Sẽ được tự động cập nhật khi có gift
      counter: 0,
      valid: false,
      gifts: [],
      startTime: new Date().getTime(),
      endTime: 253402189200000,
      closeImageUrl: '', // Thêm thuộc tính closeImageUrl
      openImageUrl: '', // Thêm thuộc tính openImageUrl
      specialAnimationUrl: '', // Thêm thuộc tính specialAnimationUrl
      displayTime: 2, // Thêm thuộc tính displayTime với giá trị mặc định 2 giây
      notiAppTime: 0, // Thêm thuộc tính notiAppTime với giá trị mặc định 0 giây
      rainPayoutRate: 0, // Tỷ lệ trả thưởng mưa
      rainRewardMin: 0, // Phần thưởng mưa tối thiểu
      rainRewardMax: 0 // Phần thưởng mưa tối đa
    });
    setGiftId('');
    setIsEditMode(false);
  };

  const addGift = async () => {
    if (!giftId.trim()) {
      toast.warning("Vui lòng nhập ID quà");
      return;
    }

    // Kiểm tra số lượng quà trong rương không được vượt quá 5
    if (newTreasure.gifts.length >= 5) {
      toast.warning("Rương đã đầy! Chỉ được phép chứa tối đa 5 món quà.");
      return;
    }

    const existingGift = newTreasure.gifts.find(gift => gift.id === giftId);
    if (existingGift) {
      toast.warning("Quà này đã có trong rương");
      return;
    }

    setLoading(prev => ({ ...prev, fetch: true }));
    try {
      const response = await TreasureService.searchGift(giftId);

      let gift = response?.data?.gift ?? {};
      if (response.status === "OK" && gift && typeof gift === 'object' && Object.keys(gift).length > 0) {
        const newGift = {
          id: gift.id,
          name: gift.name,
          imageUrl: gift.imageUrl,
          sellPrice: gift.sellPrice,
          buyPrice: gift.buyPrice || 0,
          status: gift.status,
          animatedUrl: gift.animatedUrl,
          dropRate: 0,
          noItem: 1,
          startTime: gift.startTime,
          endTime: gift.endTime,
          kind: gift.kind || "normal" // Thêm thuộc tính kind
        };
    
        setNewTreasure(prev => ({
          ...prev,
          gifts: [...prev.gifts, newGift],
          quantity: prev.gifts.length + 1 // Tự động cập nhật quantity
        }));
        setGiftId('');
        toast.success("Đã thêm quà vào rương");
      } else {
        toast.warning(response.message || "Không tìm thấy quà với ID này");
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi tìm kiếm quà");
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  const removeGift = (index) => {
    setNewTreasure(prev => ({
      ...prev,
      gifts: prev.gifts.filter((_, i) => i !== index),
      quantity: prev.gifts.length - 1 // Tự động cập nhật quantity
    }));
    toast.success("Đã xóa quà khỏi rương");
  };

  const updateGiftDropRate = (index, value) => {
    setNewTreasure(prev => ({
      ...prev,
      gifts: prev.gifts.map((gift, i) => 
        i === index ? { ...gift, dropRate: parseFloat(value) || 0 } : gift
      )
    }));
  };

  const handleImageUploaded = (imageUrl) => {
    setNewTreasure(prev => ({
      ...prev,
      imageUrl: imageUrl
    }));
  };

  const uploadImageToS3 = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (2MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file phải dưới 2MB.");
      return;
    }

    // Kiểm tra file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension)) {
      toast.error("Chỉ chấp nhận file PNG, JPG, JPEG, GIF hoặc WebP.");
      return;
    }

    // Kiểm tra file có bị corrupt không
    if (file.size === 0) {
      toast.error("File bị hỏng hoặc rỗng.");
      return;
    }

    setLoading(prev => ({ ...prev, uploadImage: true }));

    // Sử dụng fileExtension đã khai báo ở trên
    const keyName = generateGuid() + "." + fileExtension;
    const bucketName = "ikara-data/images/treasure";
    let contentType = "image/jpeg";
    
    if (fileExtension === "png") contentType = "image/png";
    else if (fileExtension === "jpg" || fileExtension === "jpeg") contentType = "image/jpeg";
    else if (fileExtension === "gif") contentType = "image/gif";
    else if (fileExtension === "webp") contentType = "image/webp";

    const fileReader = new FileReader();

    fileReader.onload = function () {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucketName', bucketName);
      formData.append('objectKey', keyName);
      formData.append('contentType', contentType);

      fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        console.log('HTTP Status:', response.status);
        console.log('Response OK:', response.ok);
        
        // Kiểm tra HTTP status code
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return response.text();
      })
      .then(responseText => {
        console.log('Upload response:', responseText);
        
        // Kiểm tra response có hợp lệ không
        if (!responseText || responseText.trim() === '') {
          throw new Error('Server trả về response rỗng');
        }
        
        // Đảm bảo URL có protocol https://
        let imageUrl = responseText.trim();
        if (imageUrl && !imageUrl.startsWith('http')) {
          imageUrl = 'https://' + imageUrl;
        }
        
        // Kiểm tra URL có đúng format không
        if (!imageUrl.includes('data4.ikara.co')) {
          throw new Error('URL không đúng format');
        }
        
        setNewTreasure(prev => ({
          ...prev,
          imageUrl: imageUrl
        }));

        setLoading(prev => ({ ...prev, uploadImage: false }));
        toast.success("Upload ảnh thành công!");
      })
      .catch(error => {
        console.error('Upload error:', error);
        setLoading(prev => ({ ...prev, uploadImage: false }));
        toast.error("Lỗi khi upload file. Vui lòng thử lại.");
      });
    };

    fileReader.readAsArrayBuffer(file);
  };

  const handleAnimatedImageUploaded = (animatedUrl) => {
    setNewTreasure(prev => ({
      ...prev,
      animatedUrl: animatedUrl
    }));
  };

  const uploadAnimatedImageToS3 = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file phải dưới 5MB.");
      return;
    }

    // Kiểm tra file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'svga' && fileExtension !== 'webp' && fileExtension !== 'gif') {
      toast.error("Chỉ chấp nhận file SVGA, WebP hoặc GIF.");
      return;
    }

    // Kiểm tra file có bị corrupt không
    if (file.size === 0) {
      toast.error("File bị hỏng hoặc rỗng.");
      return;
    }

    setLoading(prev => ({ ...prev, uploadAnimatedImage: true }));

    // Sử dụng fileExtension đã khai báo ở trên
    const keyName = generateGuid() + "." + fileExtension;
    const bucketName = "ikara-data/images/treasure";
    let contentType = "image/svga";
    
    if (fileExtension === "png") contentType = "image/png";
    else if (fileExtension === "jpg" || fileExtension === "jpeg") contentType = "image/jpeg";
    else if (fileExtension === "gif") contentType = "image/gif";
    else if (fileExtension === "webp") contentType = "image/webp";

    const fileReader = new FileReader();

    fileReader.onload = function () {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucketName', bucketName);
      formData.append('objectKey', keyName);
      formData.append('contentType', contentType);

      fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        console.log('HTTP Status:', response.status);
        console.log('Response OK:', response.ok);
        
        // Kiểm tra HTTP status code
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return response.text();
      })
      .then(responseText => {
        console.log('Upload response:', responseText);
        
        // Kiểm tra response có hợp lệ không
        if (!responseText || responseText.trim() === '') {
          throw new Error('Server trả về response rỗng');
        }
        
        // Đảm bảo URL có protocol https://
        let animatedUrl = responseText.trim();
        if (animatedUrl && !animatedUrl.startsWith('http')) {
          animatedUrl = 'https://' + animatedUrl;
        }
        
        setNewTreasure(prev => ({
          ...prev,
          animatedUrl: animatedUrl
        }));

        setLoading(prev => ({ ...prev, uploadAnimatedImage: false }));
        toast.success("Upload ảnh động thành công!");
      })
      .catch(error => {
        console.error('Upload error:', error);
        setLoading(prev => ({ ...prev, uploadAnimatedImage: false }));
        toast.error("Lỗi khi upload file. Vui lòng thử lại.");
      });
    };

    fileReader.readAsArrayBuffer(file);
  };

  const uploadCloseImageToS3 = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (2MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file phải dưới 2MB.");
      return;
    }

    // Kiểm tra file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension)) {
      toast.error("Chỉ chấp nhận file PNG, JPG, JPEG, GIF hoặc WebP.");
      return;
    }

    // Kiểm tra file có bị corrupt không
    if (file.size === 0) {
      toast.error("File bị hỏng hoặc rỗng.");
      return;
    }

    setLoading(prev => ({ ...prev, uploadCloseImage: true }));

    const keyName = generateGuid() + "." + fileExtension;
    const bucketName = "ikara-data/images/treasure";
    let contentType = "image/png";
    
    if (fileExtension === "jpg" || fileExtension === "jpeg") contentType = "image/jpeg";
    else if (fileExtension === "gif") contentType = "image/gif";
    else if (fileExtension === "webp") contentType = "image/webp";

    const fileReader = new FileReader();

    fileReader.onload = function () {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucketName', bucketName);
      formData.append('objectKey', keyName);
      formData.append('contentType', contentType);

      fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        console.log('HTTP Status:', response.status);
        console.log('Response OK:', response.ok);
        
        // Kiểm tra HTTP status code
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return response.text();
      })
      .then(responseText => {
        console.log('Upload response:', responseText);
        
        // Kiểm tra response có hợp lệ không
        if (!responseText || responseText.trim() === '') {
          throw new Error('Server trả về response rỗng');
        }
        
        // Đảm bảo URL có protocol https://
        let closeImageUrl = responseText.trim();
        if (closeImageUrl && !closeImageUrl.startsWith('http')) {
          closeImageUrl = 'https://' + closeImageUrl;
        }
        
        // Kiểm tra URL có đúng format không
        if (!closeImageUrl.includes('data4.ikara.co')) {
          throw new Error('URL không đúng format');
        }
        
        setNewTreasure(prev => ({
          ...prev,
          closeImageUrl: closeImageUrl
        }));

        setLoading(prev => ({ ...prev, uploadCloseImage: false }));
        toast.success("Upload ảnh đóng rương thành công!");
      })
      .catch(error => {
        console.error('Upload error:', error);
        setLoading(prev => ({ ...prev, uploadCloseImage: false }));
        toast.error("Lỗi khi upload file. Vui lòng thử lại.");
      });
    };

    fileReader.readAsArrayBuffer(file);
  };

  const uploadOpenImageToS3 = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (2MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file phải dưới 2MB.");
      return;
    }

    // Kiểm tra file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension)) {
      toast.error("Chỉ chấp nhận file PNG, JPG, JPEG, GIF hoặc WebP.");
      return;
    }

    // Kiểm tra file có bị corrupt không
    if (file.size === 0) {
      toast.error("File bị hỏng hoặc rỗng.");
      return;
    }

    setLoading(prev => ({ ...prev, uploadOpenImage: true }));

    const keyName = generateGuid() + "." + fileExtension;
    const bucketName = "ikara-data/images/treasure";
    let contentType = "image/png";
    
    if (fileExtension === "jpg" || fileExtension === "jpeg") contentType = "image/jpeg";
    else if (fileExtension === "gif") contentType = "image/gif";
    else if (fileExtension === "webp") contentType = "image/webp";

    const fileReader = new FileReader();

    fileReader.onload = function () {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucketName', bucketName);
      formData.append('objectKey', keyName);
      formData.append('contentType', contentType);

      fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        console.log('HTTP Status:', response.status);
        console.log('Response OK:', response.ok);
        
        // Kiểm tra HTTP status code
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return response.text();
      })
      .then(responseText => {
        console.log('Upload response:', responseText);
        
        // Kiểm tra response có hợp lệ không
        if (!responseText || responseText.trim() === '') {
          throw new Error('Server trả về response rỗng');
        }
        
        // Đảm bảo URL có protocol https://
        let openImageUrl = responseText.trim();
        if (openImageUrl && !openImageUrl.startsWith('http')) {
          openImageUrl = 'https://' + openImageUrl;
        }
        
        // Kiểm tra URL có đúng format không
        if (!openImageUrl.includes('data4.ikara.co')) {
          throw new Error('URL không đúng format');
        }
        
        setNewTreasure(prev => ({
          ...prev,
          openImageUrl: openImageUrl
        }));

        setLoading(prev => ({ ...prev, uploadOpenImage: false }));
        toast.success("Upload ảnh mở rương thành công!");
      })
      .catch(error => {
        console.error('Upload error:', error);
        setLoading(prev => ({ ...prev, uploadOpenImage: false }));
        toast.error("Lỗi khi upload file. Vui lòng thử lại.");
      });
    };

    fileReader.readAsArrayBuffer(file);
  };

  const uploadSpecialAnimationToS3 = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Kích thước file phải dưới 5MB.");
      return;
    }

    // Kiểm tra file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'svga' && fileExtension !== 'webp' && fileExtension !== 'gif') {
      toast.error("Chỉ chấp nhận file SVGA, WebP hoặc GIF.");
      return;
    }

    // Kiểm tra file có bị corrupt không
    if (file.size === 0) {
      toast.error("File bị hỏng hoặc rỗng.");
      return;
    }

    setLoading(prev => ({ ...prev, uploadSpecialAnimation: true }));

    const keyName = generateGuid() + "." + fileExtension;
    const bucketName = "ikara-data/images/treasure";
    let contentType = "image/svga";
    
    if (fileExtension === "png") contentType = "image/png";
    else if (fileExtension === "jpg" || fileExtension === "jpeg") contentType = "image/jpeg";
    else if (fileExtension === "gif") contentType = "image/gif";
    else if (fileExtension === "webp") contentType = "image/webp";

    const fileReader = new FileReader();

    fileReader.onload = function () {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucketName', bucketName);
      formData.append('objectKey', keyName);
      formData.append('contentType', contentType);

      fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        console.log('HTTP Status:', response.status);
        console.log('Response OK:', response.ok);
        
        // Kiểm tra HTTP status code
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return response.text();
      })
      .then(responseText => {
        console.log('Upload response:', responseText);
        
        // Kiểm tra response có hợp lệ không
        if (!responseText || responseText.trim() === '') {
          throw new Error('Server trả về response rỗng');
        }
        
        // Đảm bảo URL có protocol https://
        let specialAnimationUrl = responseText.trim();
        if (specialAnimationUrl && !specialAnimationUrl.startsWith('http')) {
          specialAnimationUrl = 'https://' + specialAnimationUrl;
        }
        
        setNewTreasure(prev => ({
          ...prev,
          specialAnimationUrl: specialAnimationUrl
        }));

        setLoading(prev => ({ ...prev, uploadSpecialAnimation: false }));
        toast.success("Upload ảnh động đặc biệt thành công!");
      })
      .catch(error => {
        console.error('Upload error:', error);
        setLoading(prev => ({ ...prev, uploadSpecialAnimation: false }));
        toast.error("Lỗi khi upload file. Vui lòng thử lại.");
      });
    };

    fileReader.readAsArrayBuffer(file);
  };

  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };
  


  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === "buyPrice" || name === "quantity" || name === "counter" || name === "displayTime" || name === "notiAppTime" || 
        name === "rainPayoutRate" || name === "rainRewardMin" || name === "rainRewardMax") {
      // Cho phép nhập trống hoặc số
      if (value === '') {
        value = '';
      } else {
        value = Number(value);
      }
    }
    setNewTreasure({ ...newTreasure, [name]: value });
  };

  const handleAddTreasure = async () => {
    if (!newTreasure.id || !newTreasure.name || !newTreasure.imageUrl || !newTreasure.animatedUrl || !newTreasure.closeImageUrl || !newTreasure.openImageUrl || !newTreasure.specialAnimationUrl) {
      return toast.warning("Vui lòng điền đầy đủ thông tin bắt buộc");
    }
  
    if (newTreasure.buyPrice <= 0) {
      return toast.warning("Giá mua phải lớn hơn 0");
    }

    if (!newTreasure.displayTime || newTreasure.displayTime < 1) {
      return toast.warning("Thời gian hiển thị phải từ 1 giây");
    }
    
    if (newTreasure.notiAppTime !== undefined && newTreasure.notiAppTime !== '' && (newTreasure.notiAppTime < 0)) {
      return toast.warning("Thời gian hiện noti toàn app phải từ 0 giây");
    }

    // Validation cho các trường mưa - bắt buộc điền
    if (newTreasure.rainPayoutRate === undefined || newTreasure.rainPayoutRate === '' || newTreasure.rainPayoutRate === null) {
      return toast.warning("Vui lòng nhập tỷ lệ trả thưởng mưa");
    }

    if (newTreasure.rainRewardMin === undefined || newTreasure.rainRewardMin === '' || newTreasure.rainRewardMin === null) {
      return toast.warning("Vui lòng nhập phần thưởng mưa tối thiểu");
    }

    if (newTreasure.rainRewardMax === undefined || newTreasure.rainRewardMax === '' || newTreasure.rainRewardMax === null) {
      return toast.warning("Vui lòng nhập phần thưởng mưa tối đa");
    }

    // Validation giá trị
    if (newTreasure.rainPayoutRate < 0 || newTreasure.rainPayoutRate > 100) {
      return toast.warning("Tỷ lệ trả thưởng mưa phải từ 0 đến 100%");
    }

    if (newTreasure.rainRewardMin < 0) {
      return toast.warning("Phần thưởng mưa tối thiểu phải >= 0");
    }

    if (newTreasure.rainRewardMax < 0) {
      return toast.warning("Phần thưởng mưa tối đa phải >= 0");
    }

    // Validation rainRewardMax phải lớn hơn rainRewardMin
    if (newTreasure.rainRewardMax < newTreasure.rainRewardMin) {
      return toast.warning("Phần thưởng mưa tối đa phải lớn hơn hoặc bằng phần thưởng mưa tối thiểu");
    }

  
    // Kiểm tra tổng số quà trong rương phải chính xác bằng 5
    if (!newTreasure.gifts || newTreasure.gifts.length === 0 ||newTreasure.gifts.length !== 5) {
      return toast.warning("Rương phải có chính xác 5 món quà. Hiện tại có " + newTreasure.gifts.length + " món quà.");
    }
  
  // Kiểm tra thuộc tính kind của các quà
  const invalidKinds = newTreasure.gifts.filter(gift => {
    if (!gift.kind) return true; // không có kind
    const kind = gift.kind.toLowerCase(); // chuyển về chữ thường
    return !['normal', 'rare'].includes(kind); // chỉ chấp nhận normal hoặc rare
  });

  if (invalidKinds.length > 0) {
    return toast.warning("Tất cả các món quà phải có loại hợp lệ (normal hoặc rare)");
  }
  
    // Kiểm tra thuộc tính buyPrice của các quà
    const invalidBuyPrices = newTreasure.gifts.filter(gift => gift.buyPrice === undefined || gift.buyPrice < 0);
    if (invalidBuyPrices.length > 0) {
      return toast.warning("Tất cả các món quà phải có giá mua hợp lệ (>= 0)");
    }
  
    // Tính tổng tỷ lệ (làm tròn 2 chữ số thập phân để tránh lỗi floating point)
    const totalDropRate = Math.round(newTreasure.gifts.reduce((sum, e) => sum + (parseFloat(e.dropRate) || 0), 0) * 100) / 100;
  
    if (totalDropRate !== 100) {
      return toast.warning(`Tổng tỷ lệ trúng các món quà trong rương phải là 100% (hiện tại: ${totalDropRate}%)`);
    }
  
    setOpenDialog(false);
    setLoading(prev => ({ ...prev, save: true }));
  
    try {
      let response;
      
      // Convert empty values to default values before sending to API
      const treasureData = {
        ...newTreasure,
        notiAppTime: newTreasure.notiAppTime === '' ? 0 : (newTreasure.notiAppTime || 0),
        displayTime: newTreasure.displayTime === '' ? 2 : (newTreasure.displayTime || 2),
        rainPayoutRate: newTreasure.rainPayoutRate === '' ? 0 : (newTreasure.rainPayoutRate || 0),
        rainRewardMin: newTreasure.rainRewardMin === '' ? 0 : (newTreasure.rainRewardMin || 0),
        rainRewardMax: newTreasure.rainRewardMax === '' ? 0 : (newTreasure.rainRewardMax || 0)
      };
      
      if (isEditMode) {
        // Update existing treasure
        response = await TreasureService.updateTreasure(treasureData);
        if (response.status === "OK") {
          await getTreasureList();
          toast.success("Rương đã được cập nhật thành công!");
        } else {
          toast.warning(response.message || "Lỗi khi cập nhật rương");
        }
      } else {
        // Create new treasure
        response = await TreasureService.createTreasure(treasureData);
        if (response.status === "OK") {
          await getTreasureList();
          toast.success("Rương đã được tạo thành công!");
        } else {
          toast.warning(response.message || "Lỗi khi tạo rương");
        }
      }
    } catch (error) {
      toast.error(error.message || "Đã xảy ra lỗi khi lưu rương");
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };
  

  const handleClickOpenDialog = (rowData) => {
    if (rowData) {
      // Double click on existing row - edit mode
      setNewTreasure({
        id: rowData.id,
        name: rowData.name,
        imageUrl: rowData.imageUrl,
        animatedUrl: rowData.animatedUrl,
        buyPrice: rowData.buyPrice,
        quantity: (rowData.gifts || []).length, // Tự động tính từ số lượng gift
        counter: rowData.counter,
        valid: rowData.valid,
        gifts: rowData.gifts || [],
        startTime: rowData.startTime,
        endTime: rowData.endTime,
        closeImageUrl: rowData.closeImageUrl,
        openImageUrl: rowData.openImageUrl || '',
        specialAnimationUrl: rowData.specialAnimationUrl || '',
        displayTime: rowData.displayTime || 2,
        notiAppTime: rowData.notiAppTime !== undefined ? rowData.notiAppTime : 0,
        rainPayoutRate: rowData.rainPayoutRate !== undefined ? rowData.rainPayoutRate : 0,
        rainRewardMin: rowData.rainRewardMin !== undefined ? rowData.rainRewardMin : 0,
        rainRewardMax: rowData.rainRewardMax !== undefined ? rowData.rainRewardMax : 0
      });
      setButtonName("CẬP NHẬT RƯƠNG");
      setIsEditMode(true);
    } else {
      // Click add button - create mode
      setButtonName("TẠO RƯƠNG");
      setIsEditMode(false);
    }
    setOpenDialog(true);
  };

  const handleRemoveAccounts = () => {
    console.log('Removing selected accounts:', selected);
    // Add your delete logic here
  };

  const handleDeleteTreasure = async (treasure) => {
    if (!treasure || !treasure.id) {
      toast.error("Không thể xóa rương: thiếu thông tin");
      return;
    }

    // Hiển thị confirm dialog
    const isConfirmed = window.confirm(
      `Bạn có chắc chắn muốn xóa rương "${treasure.name}" (ID: ${treasure.id})?\n\n` +
      `⚠️ Hành động này không thể hoàn tác!`
    );

    if (!isConfirmed) {
      return;
    }

    setLoading(prev => ({ ...prev, delete: true }));

    try {
      // Simulate API call delay
      // await new Promise(resolve => setTimeout(resolve, 1000));
      
      // // Xóa khỏi danh sách local
      // setRows(prevRows => prevRows.filter(row => row.id !== treasure.id));
      
      // toast.success(`Đã xóa rương "${treasure.name}" thành công!`);
      // console.log('Đã xóa rương:', treasure.id);
      
      // Nếu muốn gọi API thật, uncomment dòng dưới
      const response = await TreasureService.deleteTreasure(treasure.id);
      if (response.status === "OK") {
        toast.success(`Đã xóa rương "${treasure.name}" thành công!`);
        await getTreasureList();
      } else {
        toast.warning(response.message || "Không thể xóa rương");
      }
    } catch (error) {
      toast.error(error.message || "Lỗi khi xóa rương");
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetNewTreasure();
  };

  useEffect(() => {
    // Sử dụng dữ liệu test local thay vì gọi API
    const testData = [
      createData(
        'FUNNY', 
        "Hộp quà vui nhộn", 
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png", 
        "https://data4.ikara.co/data/minio/ikara-data/images/treasure/455299db-00a4-497d-aca0-304571751659.svga",
        50, 
        5, 
        0,
        false,
            [{
      id: 'TESTBEAR',
      name: 'Test_Gấu vui vẻ',
      imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/b4086216-552a-18ad-19e1-17e4be2ddc72.png",
      sellPrice: 10,
      buyPrice: 8,
      status: "READY",
      animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/treasure/1ec0206a-e88e-43a0-bd4f-fa2a6aeb4ff8.gif",
      dropRate: 20,
      kind: "NORMAL"
    },
    {
      id: 'TESTCAKE',
      name: 'Test_bánh',
      imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/f601613d-ed1e-49e9-c02d-182102b954e6.png",
      sellPrice: 50,
      buyPrice: 40,
      status: "READY",
      animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/test-cake.webp",
      dropRate: 20,
      kind: "normal"
    },
    {
      id: 'TESTFLOWER',
      name: 'Test_Hoa hồng',
      imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/flower.png",
      sellPrice: 30,
      buyPrice: 25,
      status: "READY",
      animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/flower.webp",
      dropRate: 20,
      kind: "RARE"
    },
    {
      id: 'TESTFLOWER2',
      name: 'Test_Hoa hồng 2',
      imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/flower.png",
      sellPrice: 30,
      buyPrice: 25,
      status: "READY",
      animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/flower.webp",
      dropRate: 20,
      kind: "normal"
    }, {
      id: 'TESTFLOWER2',
      name: 'Test_Hoa hồng 2',
      imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/flower.png",
      sellPrice: 30,
      buyPrice: 25,
      status: "READY",
      animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/flower.webp",
      dropRate: 20,
      kind: "normal"
    }],
        new Date('2025-08-12T00:00:00').getTime(),
        new Date('2025-12-31T23:59:59').getTime(),
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png",
        "https://data4.ikara.co/data/minio/ikara-data/images/treasure/525a487c-e2e4-4161-886c-7e7eae92a9b5.svga",
        2,
        0,
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png",
        15,
        100,
        500
      ),
      createData(
        'MYSTERY', 
        "Hộp quà bí ẩn", 
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/mystery-box.png", 
        "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/test-bear.svga",
        100, 
        3, 
        1,
        true,
                 [{
           id: 'MYSTERYGIFT1',
           name: 'Quà bí ẩn 1',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/mystery1.png",
           sellPrice: 25,
           buyPrice: 20,
           status: "READY",
           animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/mystery1.png",
           dropRate: 15,
           kind: "normal"
         },
         {
           id: 'MYSTERYGIFT2',
           name: 'Quà bí ẩn 2',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/mystery2.png",
           sellPrice: 35,
           buyPrice: 28,
           status: "READY",
           animatedUrl: null,
           dropRate: 25,
           kind: "normal"
         },
         {
           id: 'MYSTERYGIFT3',
           name: 'Quà bí ẩn 3',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/mystery3.png",
           sellPrice: 45,
           buyPrice: 35,
           status: "READY",
           animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/mystery3.webp",
           dropRate: 60,
           kind: "rare"
         },
         {
          id: 'MYSTERYGIFT3',
          name: 'Quà bí ẩn 3',
          imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/mystery3.png",
          sellPrice: 45,
          buyPrice: 35,
          status: "READY",
          animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/mystery3.webp",
          dropRate: 60,
          kind: "rare"
        }],
        new Date('2025-02-01T00:00:00').getTime(),
        new Date('2025-08-10T23:59:59').getTime(),
        "https://data4.ikara.co/data/minio/ikara-data/images/gifts/mystery1.png",
        null,
        2,
        0,
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png",
        25,
        200,
        800
      ),
      createData(
        'PREMIUM', 
        "Hộp quà cao cấp", 
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/premium-box.png", 
        null,
        200, 
        2, 
        0,
        false,
                 [{
           id: 'PREMIUMGIFT1',
           name: 'Quà cao cấp 1',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/premium1.png",
           sellPrice: 100,
           buyPrice: 80,
           status: "READY",
           animatedUrl: null,
           dropRate: 10,
           kind: "NORMAL"
         },
         {
           id: 'PREMIUMGIFT2',
           name: 'Quà cao cấp 2',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/premium2.png",
           sellPrice: 150,
           buyPrice: 120,
           status: "READY",
           animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/premium2.webp",
           dropRate: 5,
           kind: "normal"
         }],
        new Date('2025-01-15T10:00:00').getTime(),
        new Date('2025-12-31T23:59:59').getTime(),
        "https://data4.ikara.co/data/minio/ikara-data/images/gifts/premium1.png",
        "https://data4.ikara.co/data/minio/ikara-data/images/treasure/525a487c-e2e4-4161-886c-7e7eae92a9b5.svga",
        2,
        0,
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png",
        35,
        500,
        1500
      ),
      createData(
        'VIP', 
        "Hộp quà VIP", 
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/vip-box.png", 
        "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/vip-box.svga",
        500, 
        1, 
        5,
        true,
                 [{
           id: 'VIPGIFT1',
           name: 'Quà VIP độc quyền',
           imageUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/vip1.png",
           sellPrice: 500,
           buyPrice: 400,
           status: "READY",
           animatedUrl: "https://data4.ikara.co/data/minio/ikara-data/images/gifts/animated/vip1.webp",
           dropRate: 100,
           kind: "normal"
         }],
        new Date('2025-01-01T00:00:00').getTime(),
        new Date('2025-12-31T23:59:59').getTime(),
        "https://data4.ikara.co/data/minio/ikara-data/images/gifts/vip1.png",
        "https://data4.ikara.co/data/minio/ikara-data/images/treasure/525a487c-e2e4-4161-886c-7e7eae92a9b5.svga",
        2,
        2,
        "https://s3.us-west-002.backblazeb2.com/ikara-data/images/gifts/571c0683-5dcd-cb31-608f-6855fe355e79.png",
        50,
        1000,
        3000
      ),
    ];
    
    setRows(testData);
    console.log('Đã load dữ liệu test local:', testData);
    
    // Comment lại để không gọi API
    getTreasureList();
  }, []);

  return (
    <div className={classes.root}>
      <TabsContainer currentTab={currentTab} setCurrentTab={setCurrentTab}>
        <TabPanel value={currentTab} index={0}>
          <TreasureList
            selected={selected}
            rows={rows}
            loading={loading}
            handleClickOpenDialog={handleClickOpenDialog}
            handleRemoveAccounts={handleRemoveAccounts}
            handleDeleteTreasure={handleDeleteTreasure}
            expandedRow={expandedRow}
            handleToggleExpand={handleToggleExpand}
          />
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          <Setting />
        </TabPanel>
      </TabsContainer>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            minWidth: 800,
            maxWidth: 1000,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle style={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          padding: '20px 24px'
        }}>
          <div style={{ fontWeight: 'bold', color: '#1976d2', fontSize: '1.25rem' }}>
            {isEditMode ? '✏️ ' : '➕ '}{buttonName}
          </div>
          <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 4 }}>
            {isEditMode ? 'Chỉnh sửa thông tin rương hiện có' : 'Tạo rương mới'}
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', minHeight: '60vh' }}>
          <DialogContentText style={{ marginBottom: 20, color: '#666' }}>
            Điền thông tin cần thiết để {buttonName === "TẠO RƯƠNG" ? "tạo" : "cập nhật"} rương mới
          </DialogContentText>
          
          {/* Basic Information Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 20, 
            marginBottom: 24 
          }}>
            <TextField
              autoFocus
              margin="dense"
              label="Id Rương"
              name="id"
              placeholder={isEditMode ? "ID không thể sửa" : "Nhập id rương"}
              fullWidth
              value={newTreasure.id || ''}
              onChange={handleInputChange}
              variant="outlined"
              disabled={isEditMode}
              InputProps={{
                style: isEditMode ? { 
                  backgroundColor: '#f5f5f5',
                  color: '#666'
                } : {}
              }}
              helperText={isEditMode ? "ID rương không thể thay đổi khi cập nhật" : "Nhập ID duy nhất cho rương"}
            />
            <TextField
              margin="dense"
              label="Tên Rương"
              name="name"
              placeholder="Nhập tên rương"
              fullWidth
              value={newTreasure.name || ''}
              onChange={handleInputChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Giá mua (icoin)"
              name="buyPrice"
              type="number"
              placeholder="Nhập giá mua"
              fullWidth
              value={newTreasure.buyPrice || ''}
              onChange={handleInputChange}
              variant="outlined"
            />
            <TextField
              margin="dense"
              label="Số lượng quà"
              name="quantity"
              type="number"
              placeholder="Tự động tính từ danh sách quà"
              fullWidth
              value={newTreasure.quantity || 0}
              variant="outlined"
              disabled={true}
              InputProps={{
                style: { 
                  backgroundColor: '#f5f5f5',
                  color: '#666'
                }
              }}
              helperText="Số lượng quà được tự động tính từ danh sách quà trong rương"
            />
            <TextField
              margin="dense"
              label="Thời gian hiển thị (giây)"
              name="displayTime"
              type="number"
              placeholder="Nhập số giây hiển thị hộp quà"
              fullWidth
              value={newTreasure.displayTime || ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{ min: 1, max: 60 }}
              helperText="Thời gian hiển thị hộp quà khi mở (ít nhất 1s)"
            />
            <TextField
              margin="dense"
              label="Thời gian hiện noti toàn app (giây)"
              name="notiAppTime"
              type="number"
              placeholder="Nhập số giây hiện noti toàn app"
              fullWidth
              value={newTreasure.notiAppTime}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{ min: 0, max: 60 }}
              helperText="Thời gian hiện thông báo toàn app khi mở trúng quà hiếm (0 = không hiện)"
            />
            <TextField
              margin="dense"
              label="Tỷ lệ trả thưởng mưa (%)"
              name="rainPayoutRate"
              type="number"
              placeholder="Nhập tỷ lệ trả thưởng mưa"
              fullWidth
              value={newTreasure.rainPayoutRate !== undefined && newTreasure.rainPayoutRate !== null ? newTreasure.rainPayoutRate : ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{ min: 0, max: 100 }}
              helperText="Tỷ lệ phần trăm trả thưởng mưa (0-100%)"
            />
            <TextField
              margin="dense"
              label="Phần thưởng mưa tối thiểu"
              name="rainRewardMin"
              type="number"
              placeholder="Nhập phần thưởng mưa tối thiểu"
              fullWidth
              value={newTreasure.rainRewardMin !== undefined && newTreasure.rainRewardMin !== null ? newTreasure.rainRewardMin : ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{ min: 0 }}
              helperText="Giá trị phần thưởng mưa tối thiểu"
            />
            <TextField
              margin="dense"
              label="Phần thưởng mưa tối đa"
              name="rainRewardMax"
              type="number"
              placeholder="Nhập phần thưởng mưa tối đa"
              fullWidth
              value={newTreasure.rainRewardMax !== undefined && newTreasure.rainRewardMax !== null ? newTreasure.rainRewardMax : ''}
              onChange={handleInputChange}
              variant="outlined"
              inputProps={{ min: 0 }}
              helperText="Giá trị phần thưởng mưa tối đa"
            />
          </div>
          
          {/* Time Selection Section */}
          <div style={{ 
            marginBottom: 24,
            padding: 16,
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="subtitle1" gutterBottom style={{ 
              color: '#1976d2', 
              marginBottom: 12,
              fontWeight: 'bold'
            }}>
              ⏰ Thời gian hoạt động của rương
            </Typography>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Thời gian bắt đầu
                </label>
                <DateTimePickerComponent
                  change={(args) => {
                    if (args.value) {
                      setNewTreasure(prev => ({ 
                        ...prev, 
                        startTime: args.value.getTime() 
                      }));
                    }
                  }}
                  value={newTreasure.startTime ? new Date(newTreasure.startTime) : new Date()}
                  placeholder="Chọn thời gian bắt đầu"
                  format="dd/MM/yyyy HH:mm"
                  enableMask={true}
                  showClearButton={true}
                  cssClass="e-outline"
                />
                <small style={{ 
                  display: 'block', 
                  marginTop: '4px', 
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  Chọn thời điểm rương bắt đầu hoạt động
                </small>
              </div>
                
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Thời gian kết thúc
                </label>
                <DateTimePickerComponent
                  change={(args) => {
                    if (args.value) {
                      setNewTreasure(prev => ({ 
                        ...prev, 
                        endTime: args.value.getTime() 
                      }));
                    }
                  }}
                  value={newTreasure.endTime ? new Date(newTreasure.endTime) : null}
                  placeholder="Chọn thời gian kết thúc"
                  format="dd/MM/yyyy HH:mm"
                  enableMask={true}
                  showClearButton={true}
                  cssClass="e-outline"
                />
                <small style={{ 
                  display: 'block', 
                  marginTop: '4px', 
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  Chọn thời điểm rương kết thúc hoạt động
                </small>
              </div>
            </div>
            
            <div style={{ 
              marginTop: 12,
              padding: 12,
              backgroundColor: '#e3f2fd',
              borderRadius: 6,
              border: '1px solid #2196f3'
            }}>
              <Typography variant="caption" style={{ color: '#1976d2', display: 'block' }}>
                💡 <strong>Lưu ý:</strong> Rương sẽ chỉ hoạt động trong khoảng thời gian đã chọn. 
                Sau thời gian kết thúc, rương sẽ không còn khả dụng.
              </Typography>
            </div>
          </div>
          
          {/* Image Upload Section */}
          <div style={{ 
            marginBottom: 24,
            padding: 16,
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            backgroundColor: '#fafafa'
          }}>
            <Typography variant="subtitle1" gutterBottom style={{ 
              color: '#1976d2', 
              marginBottom: 12,
              fontWeight: 'bold'
            }}>
              🖼️ Ảnh đại diện & 🎬 Ảnh động khi mở rương
            </Typography>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 20 }}>
              {/* Ảnh đại diện */}
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold', color: '#666' }}>
                  Ảnh đại diện
                </Typography>
                <div style={{ 
                  width: '100%', 
                  maxWidth: 100,
                  position: 'relative'
                }}>
                  {newTreasure.imageUrl ? (
                    <div style={{ 
                      textAlign: 'center',
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%'
                    }}>
                      <img 
                        src={newTreasure.imageUrl} 
                        alt="Current image" 
                        style={{ 
                          width: '100%', 
                          height: 100, 
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '2px solid #1976d2',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          cursor: 'pointer'
                        }} 
                        onClick={() => document.getElementById('image-upload').click()}
                        title="Click để thay đổi ảnh"
                      />
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={uploadImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadImage}
                      />
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setNewTreasure(prev => ({ ...prev, imageUrl: '' }))}
                      title="Xóa ảnh"
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        ×
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      border: '2px dashed #1976d2',
                      borderRadius: 8,
                      padding: 12,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading.uploadImage ? 0.6 : 1,
                      height: 100,
                      width: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onClick={() => document.getElementById('image-upload').click()}
                    onMouseEnter={(e) => e.target.style.borderColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#1976d2'}
                    >
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={uploadImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadImage}
                      />
                      
                      {loading.uploadImage ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CircularProgress size={20} />
                          <Typography variant="caption">Đang upload...</Typography>
                        </div>
                      ) : (
                        <>
                          <CloudUploadIcon style={{ fontSize: 28, color: '#1976d2', marginBottom: 6 }} />
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                            Chọn ảnh
                          </Typography>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Ảnh động */}
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold', color: '#666' }}>
                  Ảnh động khi mở rương
                </Typography>
                <div style={{ 
                  width: '100%', 
                  maxWidth: 100,
                  position: 'relative'
                }}>
                  {newTreasure.animatedUrl ? (
                    <div style={{ 
                      textAlign: 'center',
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%'
                    }}>
                      {/* Ảnh động - click để xem */}
                      <div style={{
                        width: '100%',
                        height: 100,
                        borderRadius: 8,
                        border: '2px solid #1976d2',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Click để xem ảnh động"
                      >
                        <div style={{ width: '100%', height: '100%' }}>
                          <AnimatedImageDisplay
                            animatedUrl={newTreasure.animatedUrl}
                            giftName="Rương"
                            size="large"
                            showButton={false}
                          />
                        </div>
                      </div>
                      

                      
                      {/* Nút xóa - click để xóa ảnh */}
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setNewTreasure(prev => ({ ...prev, animatedUrl: '' }))}
                      title="Xóa ảnh động"
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        ×
                      </div>
                      
                      <input
                        type="file"
                        id="animated-image-upload"
                        accept="image/*,.svga,.webp,application/octet-stream"
                        onChange={uploadAnimatedImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadAnimatedImage}
                      />
                    </div>
                  ) : (
                    <div style={{
                      border: '2px dashed #1976d2',
                      borderRadius: 8,
                      padding: 12,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading.uploadAnimatedImage ? 0.6 : 1,
                      height: 100,
                      width: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onClick={() => document.getElementById('animated-image-upload').click()}
                    onMouseEnter={(e) => e.target.style.borderColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#1976d2'}
                    >
                      <input
                        type="file"
                        id="animated-image-upload"
                        accept="image/*,.svga,.webp,application/octet-stream"
                        onChange={uploadAnimatedImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadAnimatedImage}
                      />
                      
                      {loading.uploadAnimatedImage ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CircularProgress size={20} />
                          <Typography variant="caption">Đang upload...</Typography>
                        </div>
                      ) : (
                        <>
                          <CloudUploadIcon style={{ fontSize: 28, color: '#1976d2', marginBottom: 6 }} />
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                            Chọn ảnh động
                          </Typography>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Ảnh đóng rương */}
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold', color: '#666' }}>
                  Ảnh đóng rương
                </Typography>
                <div style={{ 
                  width: '100%', 
                  maxWidth: 100,
                  position: 'relative'
                }}>
                  {newTreasure.closeImageUrl ? (
                    <div style={{ 
                      textAlign: 'center',
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%'
                    }}>
                      <img 
                        src={newTreasure.closeImageUrl} 
                        alt="Close image" 
                        style={{ 
                          width: '100%', 
                          height: 100, 
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '2px solid #1976d2',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          cursor: 'pointer'
                        }} 
                        onClick={() => document.getElementById('close-image-upload').click()}
                        title="Click để thay đổi ảnh"
                      />
                      <input
                        type="file"
                        id="close-image-upload"
                        accept="image/*"
                        onChange={uploadCloseImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadCloseImage}
                      />
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setNewTreasure(prev => ({ ...prev, closeImageUrl: '' }))}
                      title="Xóa ảnh đóng rương"
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        ×
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      border: '2px dashed #1976d2',
                      borderRadius: 8,
                      padding: 12,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading.uploadCloseImage ? 0.6 : 1,
                      height: 100,
                      width: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onClick={() => document.getElementById('close-image-upload').click()}
                    onMouseEnter={(e) => e.target.style.borderColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#1976d2'}
                    >
                      <input
                        type="file"
                        id="close-image-upload"
                        accept="image/*"
                        onChange={uploadCloseImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadCloseImage}
                      />
                      
                      {loading.uploadCloseImage ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CircularProgress size={20} />
                          <Typography variant="caption">Đang upload...</Typography>
                        </div>
                      ) : (
                        <>
                          <CloudUploadIcon style={{ fontSize: 28, color: '#1976d2', marginBottom: 6 }} />
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                            Chọn ảnh
                          </Typography>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Ảnh mở rương */}
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold', color: '#666' }}>
                  Ảnh mở rương
                </Typography>
                <div style={{ 
                  width: '100%', 
                  maxWidth: 100,
                  position: 'relative'
                }}>
                  {newTreasure.openImageUrl ? (
                    <div style={{ 
                      textAlign: 'center',
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%'
                    }}>
                      <img 
                        src={newTreasure.openImageUrl} 
                        alt="Open image" 
                        style={{ 
                          width: '100%', 
                          height: 100, 
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '2px solid #1976d2',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          cursor: 'pointer'
                        }} 
                        onClick={() => document.getElementById('open-image-upload').click()}
                        title="Click để thay đổi ảnh"
                      />
                      <input
                        type="file"
                        id="open-image-upload"
                        accept="image/*"
                        onChange={uploadOpenImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadOpenImage}
                      />
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setNewTreasure(prev => ({ ...prev, openImageUrl: '' }))}
                      title="Xóa ảnh mở rương"
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        ×
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      border: '2px dashed #1976d2',
                      borderRadius: 8,
                      padding: 12,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading.uploadOpenImage ? 0.6 : 1,
                      height: 100,
                      width: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onClick={() => document.getElementById('open-image-upload').click()}
                    onMouseEnter={(e) => e.target.style.borderColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#1976d2'}
                    >
                      <input
                        type="file"
                        id="open-image-upload"
                        accept="image/*"
                        onChange={uploadOpenImageToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadOpenImage}
                      />
                      
                      {loading.uploadOpenImage ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CircularProgress size={20} />
                          <Typography variant="caption">Đang upload...</Typography>
                        </div>
                      ) : (
                        <>
                          <CloudUploadIcon style={{ fontSize: 28, color: '#1976d2', marginBottom: 6 }} />
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                            Chọn ảnh
                          </Typography>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Special Animation URL */}
              <div>
                <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 'bold', color: '#666' }}>
                  Ảnh động đặc biệt mở rương
                </Typography>
                <div style={{ 
                  width: '100%', 
                  maxWidth: 100,
                  position: 'relative'
                }}>
                  {newTreasure.specialAnimationUrl ? (
                    <div style={{ 
                      textAlign: 'center',
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%'
                    }}>
                      {/* Ảnh động đặc biệt - click để xem */}
                      <div style={{
                        width: '100%',
                        height: 100,
                        borderRadius: 8,
                        border: '2px solid #1976d2',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Click để xem ảnh động đặc biệt"
                      >
                        <div style={{ width: '100%', height: '100%' }}>
                          <AnimatedImageDisplay
                            animatedUrl={newTreasure.specialAnimationUrl}
                            giftName="Rương đặc biệt"
                            size="large"
                            showButton={false}
                          />
                        </div>
                      </div>
                      

                      
                      {/* Nút xóa - click để xóa ảnh */}
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => setNewTreasure(prev => ({ ...prev, specialAnimationUrl: '' }))}
                      title="Xóa ảnh động đặc biệt"
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        ×
                      </div>
                      
                      <input
                        type="file"
                        id="special-animation-upload"
                        accept="image/*,.svga,.webp,application/octet-stream"
                        onChange={uploadSpecialAnimationToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadSpecialAnimation}
                      />
                    </div>
                  ) : (
                    <div style={{
                      border: '2px dashed #1976d2',
                      borderRadius: 8,
                      padding: 12,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading.uploadSpecialAnimation ? 0.6 : 1,
                      height: 100,
                      width: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onClick={() => document.getElementById('special-animation-upload').click()}
                    onMouseEnter={(e) => e.target.style.borderColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#1976d2'}
                    >
                      <input
                        type="file"
                        id="special-animation-upload"
                        accept="image/*,.svga,.webp,application/octet-stream"
                        onChange={uploadSpecialAnimationToS3}
                        style={{ display: 'none' }}
                        disabled={loading.uploadSpecialAnimation}
                      />
                      
                      {loading.uploadSpecialAnimation ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CircularProgress size={20} />
                          <Typography variant="caption">Đang upload...</Typography>
                        </div>
                      ) : (
                        <>
                          <CloudUploadIcon style={{ fontSize: 28, color: '#1976d2', marginBottom: 6 }} />
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                            Chọn ảnh động đặc biệt
                          </Typography>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Gift Management Section */}
          <div style={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 16,
            backgroundColor: '#fff'
          }}>
            <div style={{ 
              color: '#1976d2',
              fontWeight: 'bold',
              marginBottom: 16,
              fontSize: '1.25rem'
            }}>
              🎁 Quản lý quà trong rương
            </div>
            
            {/* Add Gift Input */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              marginBottom: 20,
              alignItems: 'flex-end'
            }}>
              <TextField
                margin="dense"
                label="ID Quà"
                placeholder="Nhập ID quà tặng"
                value={giftId || ''}
                onChange={(e) => setGiftId(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    addGift();
                  }
                }}
                style={{ flex: 1 }}
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={addGift}
                style={{ 
                  height: 40,
                  minWidth: 120,
                  fontWeight: 'bold'
                }}
              >
                + Thêm Quà
              </Button>
            </div>
            
            {/* Gift List */}
            <div style={{ 
              maxHeight: 300, 
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              padding: 8
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 12
              }}>
                {newTreasure.gifts.map((gift, index) => (
                  <div key={gift.id} style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 6, 
                    padding: 10, 
                    border: '1px solid #ddd', 
                    borderRadius: 6, 
                    backgroundColor: '#fafafa',
                    minHeight: 160
                  }}>
                    {/* Header Row - Image, Name, ID, Type */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img 
                        src={gift.imageUrl} 
                        alt={gift.name} 
                        style={{ 
                          width: 40, 
                          height: 40, 
                          objectFit: 'cover', 
                          borderRadius: 4,
                          border: '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }} 
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" style={{ 
                          fontWeight: 'bold', 
                          marginBottom: 2,
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {gift.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                          ID: {gift.id}
                        </Typography>
                        <div style={{ marginTop: 2 }}>
                          <Chip
                            label={(() => {
                              if (!gift.kind || gift.kind === '') {
                                return 'Không có giá trị';
                              } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                return 'Hiếm';
                              } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                return 'Bình thường';
                              } else {
                                return 'Không có giá trị';
                              }
                            })()}
                            style={{
                              backgroundColor: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '#f5f5f5';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                  return '#fff3e0';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '#e8f5e8';
                                } else {
                                  return '#f5f5f5';
                                }
                              })(),
                              color: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '#999';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE' ) {
                                  return '#f57c00';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '#4caf50';
                                } else {
                                  return '#999';
                                }
                              })(),
                              fontWeight: 'bold',
                              fontSize: '10px',
                              padding: '1px 4px',
                              height: '20px',
                              border: (() => {
                                if (!gift.kind || gift.kind === '') {
                                  return '1px solid #99920';
                                } else if (gift.kind === 'rare' || gift.kind === 'RARE') {
                                  return '1px solid #f57c0020';
                                } else if (gift.kind === 'normal' || gift.kind === 'NORMAL') {
                                  return '1px solid #4caf5020';
                                } else {
                                  return '1px solid #99920';
                                }
                              })()
                            }}
                            size="small"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Middle Row - Prices and Animated Image */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '6px 0',
                      borderTop: '1px solid #eee',
                      borderBottom: '1px solid #eee'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                          Mua: {gift.buyPrice || 0} icoin
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                          Bán: {gift.sellPrice || 0} icoin
                        </Typography>
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem' }}>
                          Ảnh động
                        </Typography>
                        <AnimatedImageDisplay 
                          animatedUrl={gift.animatedUrl}
                          giftName={gift.name}
                          size="small"
                          showButton={false}
                        />
                      </div>
                    </div>

                    {/* Bottom Row - Drop Rate and Delete */}
                    <div style={{ 
                      display: 'flex', 
                      gap: 8, 
                      alignItems: 'center',
                      paddingTop: 6,
                      borderTop: '1px solid #eee'
                    }}>
                      <TextField
                        type="text"
                        label="Tỷ lệ (%)"
                        value={gift.dropRate === 0 ? '' : gift.dropRate || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Cho phép nhập số thập phân và để trống
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const numValue = value === '' ? 0 : parseFloat(value) || 0;
                            // Kiểm tra giới hạn 0-100
                            if (numValue >= 0 && numValue <= 100) {
                              updateGiftDropRate(index, value === '' ? 0 : value);
                            }
                          }
                        }}
                        inputProps={{ 
                          min: 0, 
                          max: 100,
                          style: { fontSize: '0.8rem', padding: '6px 8px' }
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: { fontSize: '0.8rem' }
                        }}
                        style={{ flex: 1 }}
                        size="small"
                        variant="outlined"
                        placeholder="0-100"
                      />
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => removeGift(index)}
                        style={{ 
                          minWidth: 50,
                          height: 32,
                          fontSize: '0.75rem'
                        }}
                      >
                        Xóa
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {newTreasure.gifts.length === 0 && (
                <div style={{
                  textAlign: 'center', 
                  padding: 40,
                  color: '#999',
                  border: '2px dashed #ddd',
                  borderRadius: 6,
                  margin: 8
                }}>
                  <Typography variant="body2" color="textSecondary">
                    📦 Chưa có quà nào trong rương
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                    Thêm quà bằng cách nhập ID quà ở trên
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        
        <DialogActions style={{ 
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Button 
            onClick={handleCloseDialog} 
            color="primary" 
            disabled={loading.save || loading.delete}
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            Huỷ
          </Button>
          <Button 
            onClick={handleAddTreasure} 
            color="primary"
            disabled={loading.save}
            variant="contained"
            style={{ minWidth: 120, fontWeight: 'bold' }}
          >
            {loading.save ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={16} color="inherit" />
                Đang lưu...
              </div>
            ) : (
              buttonName
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
  
}


export default ManageTreasure;
